import React, { useState, useEffect, useRef } from "react";
import API from "../api";
import { toast } from "react-hot-toast";
import { capture } from "../instrument";

const HandwrittenAnnotation = () => {
  const [annotation, setAnnotation] = useState(null);
  const [correctedText, setCorrectedText] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const textareaRef = useRef(null);
  const [goToPage, setGoToPage] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [currentWord, setCurrentWord] = useState("");
  const [wordStart, setWordStart] = useState(0);
  const suggestionsRef = useRef(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);

  const fetchAnnotation = async () => {
    try {
      setLoading(true);
      const response = await API.get(`/htr?offset=${offset}&size=1&q=${search}`);

      if (!response?.ok) {
        toast.error("No annotations found");
        return setAnnotation(null);
      }

      const doc = response.data.docs[0];
      setAnnotation(doc);
      setCorrectedText(doc.correctedText || doc.originalText || "");

      if (response.data.total) setTotal(response.data.total);
    } catch (error) {
      capture(error);
      console.error(error);
      toast.error("Error fetching annotation");
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalCount = async () => {
    try {
      const response = await API.get(`/htr?getTotal=true&q=${search}`);
      if (response.data && response.data.data) {
        setTotal(response.data.data.total);
      }
    } catch (error) {
      capture(error);
      console.error(error);
    }
  };

  const saveAnnotation = async (status = "completed") => {
    try {
      if (!annotation) return;

      setSaving(true);
      const response = await API.put(`/htr/${annotation._id}`, {
        correctedText,
        annotationStatus: status,
      });

      if (response.data && response.ok) {
        toast.success(status === "completed" ? "Saved and moving to next" : "Draft saved");

        // Move to next annotation if requested
        if (status === "completed" && offset < total - 1) {
          setOffset((prev) => prev + 1);
        }
      }
    } catch (error) {
      capture(error);
      console.error(error);
      toast.error("Error saving annotation");
    } finally {
      setSaving(false);
    }
  };

  const fetchSuggestions = async (word) => {
    if (!word || word.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await API.get(`/htr/autocomplete?q=${encodeURIComponent(word)}&sourceId=${annotation?.sourceId}`);
      if (response.ok && response.data) {
        setSuggestions(response.data);
        setShowSuggestions(true);
        setActiveSuggestionIndex(0);
      }
    } catch (error) {
      capture(error);
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setCorrectedText(value);

    // Get cursor position
    const curPos = e.target.selectionStart;
    setCursorPosition(curPos);

    // Extract the current word being typed
    const textBeforeCursor = value.substring(0, curPos);

    // Find the start of the current word by looking for space or start of text
    // This regex matches the last word before cursor, handling punctuation
    const wordMatch = textBeforeCursor.match(/[^\s,.;:!?()[\]{}'"«»""]+$/);

    if (wordMatch) {
      const word = wordMatch[0];
      const wordStartPos = textBeforeCursor.length - word.length;

      setCurrentWord(word);
      setWordStart(wordStartPos);

      // Fetch suggestions for the current word
      fetchSuggestions(word);
    } else {
      // No word is being typed
      setCurrentWord("");
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    // Show keyboard shortcuts on Alt+K
    if (e.altKey && e.key === "k") {
      e.preventDefault();
      setShowKeyboardShortcuts(true);
      return;
    }

    // Tab to select suggestion
    if (e.key === "Tab" && showSuggestions && suggestions.length > 0) {
      e.preventDefault();
      applySuggestion(suggestions[activeSuggestionIndex]);
      return;
    }

    // Navigate through suggestions with arrow keys
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => (prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0));
        return;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1));
        return;
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      } else if (e.key === "Enter" && showSuggestions) {
        e.preventDefault();
        applySuggestion(suggestions[activeSuggestionIndex]);
        return;
      }
    }

    // Save on Ctrl+S
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      saveAnnotation();
      return;
    }

    // Delete annotation on Alt+Delete or Alt+Backspace
    if (e.altKey && (e.key === "Delete" || e.key === "Backspace")) {
      e.preventDefault();
      if (annotation) {
        if (window.confirm("Are you sure you want to delete this annotation?")) {
          API.delete(`/htr/${annotation._id}`)
            .then(() => {
              toast.success("Annotation deleted");
              fetchAnnotation();
            })
            .catch((error) => {
              console.error(error);
              toast.error("Error deleting annotation");
            });
        }
      }
      return;
    }

    // Previous annotation on Alt+Left
    if (e.altKey && e.key === "ArrowLeft") {
      e.preventDefault();
      if (offset > 0) {
        setOffset((prev) => prev - 1);
      }
      return;
    }

    // Next annotation on Alt+Right
    if (e.altKey && e.key === "ArrowRight") {
      e.preventDefault();
      if (offset < total - 1) {
        setOffset((prev) => prev + 1);
      }
      return;
    }
  };

  const applySuggestion = (suggestion) => {
    if (!suggestion) return;

    const before = correctedText.substring(0, wordStart);
    const after = correctedText.substring(cursorPosition);

    // Check if we need to add a space after the suggestion
    // Don't add space if there's already one or if there's punctuation
    const needsSpace = after.length === 0 || (!after.startsWith(" ") && ![",", ".", ";", ":", "!", "?", ")", "]", "}", '"', "'", "»", '"', '"'].includes(after[0]));

    const newText = before + suggestion.value + (needsSpace ? " " : "") + after;

    setCorrectedText(newText);
    setShowSuggestions(false);

    // Reset cursor position after the applied suggestion
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPosition = wordStart + suggestion.value.length + (needsSpace ? 1 : 0);
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        setCursorPosition(newCursorPosition);
      }
    }, 0);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) && textareaRef.current && !textareaRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchAnnotation();
  }, [offset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== "") {
        fetchTotalCount();
        setOffset(0);
        fetchAnnotation();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [offset, total, annotation, correctedText, suggestions, showSuggestions, activeSuggestionIndex]);

  useEffect(() => {
    // Focus the textarea when annotation changes
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [annotation]);

  useEffect(() => {
    // Update goToPage when offset changes
    setGoToPage(offset + 1);
  }, [offset]);

  if (loading && !annotation) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading annotations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-16">
      <div className="flex justify-end mb-6">
        <button onClick={() => setShowKeyboardShortcuts(true)} className="text-gray-500 hover:text-gray-700 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Keyboard Shortcuts
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center mb-4">
          <div className="relative w-full">
            <input
              type="text"
              className="w-full p-3 pl-10 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
              placeholder="Search by text..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {search && (
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSearch("")}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center bg-gray-100 px-3 py-2 rounded-lg whitespace-nowrap">
            <input
              type="number"
              className="w-16 h-8 bg-gray-100 text-center font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded"
              min={1}
              max={total}
              value={goToPage || ""}
              onChange={(e) => {
                const val = e.target.value === "" ? "" : parseInt(e.target.value);
                setGoToPage(val);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (goToPage && goToPage > 0 && goToPage <= total) {
                    setOffset(goToPage - 1);
                  }
                }
              }}
              onBlur={() => {
                if (goToPage && goToPage > 0 && goToPage <= total) {
                  setOffset(goToPage - 1);
                } else {
                  setGoToPage(offset + 1);
                }
              }}
            />
            <span className="text-gray-500 font-medium"> / {total}</span>
          </div>
        </div>
      </div>

      {annotation ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Image</h2>
              {annotation.imageS3Url && (
                <a href={annotation.imageS3Url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open Full Size
                </a>
              )}
            </div>
            {annotation.imageS3Url ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative group">
                <img src={annotation.imageS3Url} alt="Handwritten text" className="max-w-full h-auto mx-auto" />
              </div>
            ) : (
              <div className="flex justify-center items-center h-48 bg-gray-50 text-gray-400 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">Annotation</h2>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="block font-semibold text-gray-700">Corrected Text:</label>
                  <span className="text-sm text-gray-500">{correctedText.length} characters</span>
                </div>
                <div className="relative">
                  <input
                    ref={textareaRef}
                    type="text"
                    className="w-full p-4 border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    value={correctedText}
                    onChange={handleTextareaChange}
                    onKeyDown={(e) => {
                      // Handle textarea-specific keyboard events
                      if (showSuggestions && suggestions.length > 0) {
                        if (e.key === "Tab" || (e.key === "Enter" && showSuggestions) || e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Escape") {
                          // Let the global handler take care of these
                          return;
                        }
                      }

                      // For other keys, let them work normally in the textarea
                    }}
                    placeholder="Enter the correct transcription here..."
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto w-72"
                      style={{
                        left: `${cursorPosition * 8}px`, // Approximate position based on character width
                        top: "calc(100% + 5px)", // Position below the textarea
                      }}
                    >
                      <div className="p-2 border-b border-gray-100 flex justify-between items-center">
                        <span className="text-xs font-medium text-indigo-600">Suggestions</span>
                        <div className="flex gap-1">
                          <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">↑↓</span>
                          <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Tab</span>
                          <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Esc</span>
                        </div>
                      </div>
                      <ul className="py-1">
                        {suggestions.map((suggestion, index) => (
                          <li
                            key={index}
                            className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 flex items-center transition-colors ${
                              index === activeSuggestionIndex ? "bg-indigo-50 text-indigo-700 border-l-2 border-indigo-500" : "text-gray-700"
                            }`}
                            onClick={() => applySuggestion(suggestion)}
                          >
                            <div className="flex-grow font-medium flex items-center gap-2">
                              {suggestion.value}
                              {suggestion.isCodeAddedInSourceId ? (
                                <span className="inline-flex h-2 w-2 rounded-full bg-orange-500 ring-1 ring-white" title="Code exists in source"></span>
                              ) : suggestion.isCodeInSourceId ? (
                                <span className="inline-flex h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" title="Code exists in source"></span>
                              ) : null}
                            </div>
                            {index === activeSuggestionIndex && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Tab</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex justify-end">
                  <span className="text-xs text-gray-500 italic">Press Tab to complete suggestions</span>
                </div>
              </div>

              <div className="flex flex-wrap justify-between gap-3">
                <div className="flex gap-2">
                  <button
                    className="flex items-center gap-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    onClick={() => saveAnnotation("pending")}
                    disabled={saving}
                  >
                    {saving ? (
                      <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                        />
                      </svg>
                    )}
                    Save Draft
                  </button>
                  <button
                    className="flex items-center gap-1 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this annotation?")) {
                        API.delete(`/htr/${annotation._id}`)
                          .then(() => {
                            toast.success("Annotation deleted");
                            fetchAnnotation();
                          })
                          .catch((error) => {
                            console.error(error);
                            toast.error("Error deleting annotation");
                          });
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
                <button
                  className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => saveAnnotation("completed")}
                  disabled={!correctedText.trim() || saving}
                >
                  {saving ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  Save & Next <span className="text-xs bg-indigo-500 px-1.5 py-0.5 rounded">Ctrl+S</span>
                </button>
              </div>
            </div>
          </div>

          {annotation.sourceId && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden lg:col-span-2">
              <div className="border-b border-gray-100 px-6 py-4">
                <h2 className="text-xl font-semibold text-gray-800">Metadata</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex">
                      <span className="font-medium text-gray-500 w-32">Source ID:</span>
                      <button
                        onClick={() => window.open(`/prescription/${annotation.sourceId}`, "_blank")}
                        className="text-primary-600 hover:text-primary-800 transition-colors flex items-center gap-2"
                        title="View in Explorer"
                      >
                        {annotation.sourceId}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-gray-500 w-32">Score :</span>
                      <span className="text-gray-800">{annotation.modelConfidence}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-gray-500 w-32">Status:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          annotation.annotationStatus === "completed"
                            ? "bg-green-100 text-green-800"
                            : annotation.annotationStatus === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {annotation.annotationStatus}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex">
                      <span className="font-medium text-gray-500 w-32">Created:</span>
                      <span className="text-gray-800">{new Date(annotation.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-gray-500 w-32">Updated:</span>
                      <span className="text-gray-800">{new Date(annotation.updatedAt).toLocaleString()}</span>
                    </div>
                    {annotation.annotatedBy && (
                      <div className="flex">
                        <span className="font-medium text-gray-500 w-32">Annotated By:</span>
                        <span className="text-gray-800">{annotation.annotatedBy}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 text-lg">No annotations found</p>
          <button
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            onClick={() => {
              setSearch("");
              setOffset(0);
              fetchAnnotation();
            }}
          >
            Clear search and try again
          </button>
        </div>
      )}

      {/* Keyboard shortcuts modal */}
      {showKeyboardShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Keyboard Shortcuts</h3>
              <button onClick={() => setShowKeyboardShortcuts(false)} className="text-gray-500 hover:text-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Save annotation</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Ctrl + S</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Delete annotation</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Alt + Delete</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Previous annotation</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Alt + ←</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Next annotation</span>
                <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Alt + →</span>
              </div>
              <div className="border-t border-gray-200 my-2 pt-2 space-y-3">
                <h4 className="font-medium text-gray-700 mb-2">Autocomplete</h4>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Select suggestion</span>
                  <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Tab</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Navigate suggestions</span>
                  <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">↑ / ↓</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Close suggestions</span>
                  <span className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">Esc</span>
                </div>
              </div>
            </div>
            <button className="mt-6 w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors" onClick={() => setShowKeyboardShortcuts(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandwrittenAnnotation;
